import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createNadDebug } from "../../../../utils/debugLog";

const previewLog = createNadDebug("dw:voice-preview");

export type VoicePreviewPhase =
  | "idle"
  | "loading"
  | "playing"
  | "paused"
  | "error";

export interface VoicePreviewState {
  voiceId: string | null;
  phase: VoicePreviewPhase;
}

export interface VoicePreviewController {
  state: VoicePreviewState;
  toggle: (voiceId: string, previewUrl: string) => void;
  stop: () => void;
}

export type VoicePreviewAudio = Pick<
  HTMLAudioElement,
  "currentTime" | "onended" | "onerror" | "pause" | "play"
>;

export type VoicePreviewAudioFactory = (
  previewUrl: string,
) => VoicePreviewAudio;

const createBrowserAudio: VoicePreviewAudioFactory = (previewUrl) =>
  new Audio(previewUrl);

const IDLE_STATE: VoicePreviewState = {
  voiceId: null,
  phase: "idle",
};

export function useVoicePreview(
  createAudio: VoicePreviewAudioFactory = createBrowserAudio,
): VoicePreviewController {
  const audioRef = useRef<VoicePreviewAudio | null>(null);
  const stateRef = useRef<VoicePreviewState>(IDLE_STATE);
  const playAttemptRef = useRef(0);
  const [state, setState] = useState<VoicePreviewState>(IDLE_STATE);

  const updateState = useCallback((next: VoicePreviewState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const releaseAudio = useCallback(() => {
    playAttemptRef.current += 1;
    const audio = audioRef.current;
    if (!audio) return;
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    audio.currentTime = 0;
    audioRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const voiceId = stateRef.current.voiceId;
    releaseAudio();
    updateState(IDLE_STATE);
    if (voiceId) {
      previewLog({
        event: "voice-preview:stopped",
        voice_id: voiceId,
        reason: "closed",
      });
    }
  }, [releaseAudio, updateState]);

  const play = useCallback(
    (audio: VoicePreviewAudio, voiceId: string) => {
      const playAttempt = ++playAttemptRef.current;
      updateState({ voiceId, phase: "loading" });
      previewLog({
        event: "voice-preview:requested",
        voice_id: voiceId,
      });
      void audio.play().then(
        () => {
          if (
            playAttemptRef.current !== playAttempt ||
            audioRef.current !== audio ||
            stateRef.current.voiceId !== voiceId ||
            stateRef.current.phase !== "loading"
          )
            return;
          updateState({ voiceId, phase: "playing" });
          previewLog({
            event: "voice-preview:started",
            voice_id: voiceId,
          });
        },
        () => {
          if (
            playAttemptRef.current !== playAttempt ||
            audioRef.current !== audio ||
            stateRef.current.voiceId !== voiceId ||
            stateRef.current.phase !== "loading"
          )
            return;
          releaseAudio();
          updateState({ voiceId, phase: "error" });
          previewLog({
            event: "voice-preview:failed",
            voice_id: voiceId,
          });
        },
      );
    },
    [releaseAudio, updateState],
  );

  const toggle = useCallback(
    (voiceId: string, previewUrl: string) => {
      const current = audioRef.current;
      const currentState = stateRef.current;
      if (
        current &&
        currentState.voiceId === voiceId &&
        (currentState.phase === "playing" || currentState.phase === "loading")
      ) {
        playAttemptRef.current += 1;
        current.pause();
        updateState({ voiceId, phase: "paused" });
        previewLog({
          event: "voice-preview:stopped",
          voice_id: voiceId,
          reason: "paused",
        });
        return;
      }
      if (
        current &&
        currentState.voiceId === voiceId &&
        currentState.phase === "paused"
      ) {
        play(current, voiceId);
        return;
      }

      const previousVoiceId = currentState.voiceId;
      releaseAudio();
      if (previousVoiceId) {
        previewLog({
          event: "voice-preview:stopped",
          voice_id: previousVoiceId,
          reason: "replaced",
        });
      }
      const audio = createAudio(previewUrl);
      audioRef.current = audio;
      audio.onended = () => {
        if (audioRef.current !== audio) return;
        releaseAudio();
        updateState(IDLE_STATE);
        previewLog({
          event: "voice-preview:stopped",
          voice_id: voiceId,
          reason: "ended",
        });
      };
      audio.onerror = () => {
        if (audioRef.current !== audio) return;
        releaseAudio();
        updateState({ voiceId, phase: "error" });
        previewLog({
          event: "voice-preview:failed",
          voice_id: voiceId,
        });
      };
      play(audio, voiceId);
    },
    [createAudio, play, releaseAudio, updateState],
  );

  useEffect(
    () => () => {
      releaseAudio();
    },
    [releaseAudio],
  );

  return useMemo(
    () => ({
      state,
      toggle,
      stop,
    }),
    [state, stop, toggle],
  );
}
